import {
  MacroPackageMessageType, RawPackageMessageType, generateMacroPackageMessage, macroPackageResponseSchema, rawPackageUploadProgressSchema,
  type MacroPackageServerError, type MacroTrigger, type PlaybackAssetGrant, type PlaycastMessage, type PlaycastMessageTarget,
  type WebsocketMessage,
  type HostRecordingCatalogPage,
} from '@playcastdotio/Messaging';
import type { IRealtimeService } from '../realtime/IRealtimeService';
import type { IMessageRouter } from '../MessageRouter';
import type { IIpcService } from '../ipc/IIpcService';
import { playcastEnvelope } from './NativeMacroPackageBridge';
import { RawPackageCatalogIpcClient } from './RawPackageCatalogIpcClient';

export const MACRO_PACKAGE_GRANT_TIMEOUT_MS = 15_000;

export interface MacroPackageGrantRequest {
  campaignId: string; trigger: MacroTrigger; variant: 'authoritative' | 'preview'; packageSha256?: string;
}
export type MacroPackageGrantResult =
  | { ok: true; packageKey: string; packageSha256: string; grant: PlaybackAssetGrant }
  | { ok: false; error: MacroPackageServerError | 'grant_request_timeout' | 'package_digest_mismatch' };

interface PendingGrant { request: MacroPackageGrantRequest; resolve(result: MacroPackageGrantResult): void; timer: ReturnType<typeof setTimeout> }

/**
 * `MACROPACKAGE_REQUEST` → `MACROPACKAGE_RESPONSE` over the host's authenticated socket, correlated by
 * `requestId`. Several requests may be in flight (one per starting session). An unanswered request fails
 * with `grant_request_timeout`; the realtime service queues sends while it reconnects.
 */
export class MacroPackageSocketLoader {
  private readonly pending = new Map<string, PendingGrant>();

  public constructor(private readonly realtime: Pick<IRealtimeService, 'sendMessage'>, router: IMessageRouter,
    private readonly timeoutMs = MACRO_PACKAGE_GRANT_TIMEOUT_MS) {
    router.register(MacroPackageMessageType.RESPONSE, (message) => this.receive(message));
  }

  public request(request: MacroPackageGrantRequest): Promise<MacroPackageGrantResult> {
    const requestId = crypto.randomUUID();
    return new Promise((resolve) => {
      const timer = setTimeout(() => this.settle(requestId, { ok: false, error: 'grant_request_timeout' }), this.timeoutMs);
      this.pending.set(requestId, { request, resolve, timer });
      try {
        this.realtime.sendMessage(generateMacroPackageMessage({ type: MacroPackageMessageType.REQUEST, requestId,
          campaignId: request.campaignId, trigger: request.trigger, variant: request.variant,
          ...(request.packageSha256 ? { packageSha256: request.packageSha256 } : {}) }));
      } catch {
        this.settle(requestId, { ok: false, error: 'package_not_found' });
      }
    });
  }

  private receive(message: unknown): void {
    const parsed = macroPackageResponseSchema.safeParse(message);
    if (!parsed.success) return;
    const response = parsed.data;
    const pending = this.pending.get(response.requestId);
    if (!pending) return;
    if (!response.ok) return this.settle(response.requestId, { ok: false, error: response.error });
    const expected = pending.request.packageSha256;
    if (expected && expected !== response.packageSha256) return this.settle(response.requestId, { ok: false, error: 'package_digest_mismatch' });
    this.settle(response.requestId, { ok: true, packageKey: response.packageKey, packageSha256: response.packageSha256, grant: response.grant });
  }

  private settle(requestId: string, result: MacroPackageGrantResult): void {
    const pending = this.pending.get(requestId);
    if (!pending) return;
    this.pending.delete(requestId);
    clearTimeout(pending.timer);
    pending.resolve(result);
  }
}

/** IPC action names shared with host-electron `hostHandler.ts`. */
export const MACRO_PACKAGE_IPC = {
  upload: 'macroPackageUpload', uploadComplete: 'macroPackageUploadComplete', socket: 'rawPackageSocket',
} as const;
export interface RawPackageUploadClient {
  upload(packageId: string, campaignId: string, uploadGrantToken?: string, expectedManifestSha256?: string): Promise<{ ok: true } | { ok: false; error: string }>;
  stage?(packageId: string, campaignId: string, manifestSha256: string): 'uploading' | 'verifying';
  catalog?(campaignId: string, cursor?: string): Promise<HostRecordingCatalogPage>;
}

/**
 * Renderer half of the raw package upload. host-electron streams the files from disk; this bridge asks it to
 * start, relays its `RAWPACKAGE_UPLOAD_BEGIN/COMPLETE` onto the realtime socket, and returns `GRANTED/INGESTED`.
 */
export class RawPackageUploadSocketBridge implements RawPackageUploadClient {
  private readonly uploads = new Map<string, { packageId: string; campaignId: string; manifestSha256?: string; stage: 'uploading' | 'verifying';
    resolve(result: { ok: true } | { ok: false; error: string }): void }>();
  private readonly requests = new Map<string, { generation: number; ownerRequestId: string }>();
  private generation = 0;
  private readonly catalogReader: RawPackageCatalogIpcClient;

  public constructor(private readonly ipc: IIpcService, private readonly realtime: Pick<IRealtimeService, 'sendMessage'>
    & Partial<Pick<IRealtimeService, 'trySendMessage' | 'onDisconnected'>>,
    router: IMessageRouter) {
    this.catalogReader = new RawPackageCatalogIpcClient(ipc);
    const toMain = (message: WebsocketMessage) => this.ipc.send(playcastEnvelope('host', MACRO_PACKAGE_IPC.socket, true, { message }));
    router.register(RawPackageMessageType.UPLOAD_GRANTED, toMain);
    router.register(RawPackageMessageType.INGESTED, toMain);
    router.register(RawPackageMessageType.UPLOAD_PROGRESS, message => { this.progress(message); toMain(message); });
    realtime.onDisconnected?.(() => {
      this.generation++;
      for (const upload of this.uploads.values()) upload.resolve({ ok: false, error: 'raw_package_upload_disconnected' });
      this.uploads.clear();
      this.requests.clear();
      this.catalogReader.disconnect();
      this.ipc.send(playcastEnvelope('host', MACRO_PACKAGE_IPC.socket, true, { message: { disconnected: true } }));
    });
  }

  public upload(packageId: string, campaignId: string, uploadGrantToken?: string, expectedManifestSha256?: string): Promise<{ ok: true } | { ok: false; error: string }> {
    const requestId = crypto.randomUUID();
    return new Promise((resolve) => {
      this.uploads.set(requestId, { resolve, packageId, campaignId, manifestSha256: expectedManifestSha256, stage: 'uploading' });
      try {
        this.ipc.send(playcastEnvelope('host', MACRO_PACKAGE_IPC.upload, false, { requestId, packageId, campaignId,
          ...(uploadGrantToken ? { uploadGrantToken } : {}), ...(expectedManifestSha256 ? { expectedManifestSha256 } : {}) }));
      } catch {
        this.uploads.delete(requestId);
        resolve({ ok: false, error: 'raw_package_upload_unavailable' });
      }
    });
  }

  public stage(packageId: string, campaignId: string, manifestSha256: string): 'uploading' | 'verifying' {
    return [...this.uploads.values()].some(upload => upload.packageId === packageId && upload.campaignId === campaignId
      && upload.manifestSha256 === manifestSha256 && upload.stage === 'verifying') ? 'verifying' : 'uploading';
  }

  public catalog(campaignId: string, cursor?: string): Promise<HostRecordingCatalogPage> { return this.catalogReader.page(campaignId, cursor); }

  private progress(message: unknown): void {
    const parsed = rawPackageUploadProgressSchema.safeParse(message);
    if (!parsed.success) return;
    const owner = this.requests.get(parsed.data.requestId);
    if (!owner || owner.generation !== this.generation) return;
    const upload = this.uploads.get(owner.ownerRequestId);
    if (upload?.packageId === parsed.data.packageId && upload.campaignId === parsed.data.campaignId
      && upload.manifestSha256 === parsed.data.manifestSha256) upload.stage = parsed.data.stage === 'verifying' ? 'verifying' : 'uploading';
  }

  /** Consumes host-electron messages addressed to this bridge; returns false for anything else. */
  public receive(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    if (this.catalogReader.receive(message)) return true;
    if (message.header.target !== 'host') return false;
    const action = message.header.action as string;
    const body = (message.body.message ?? {}) as Record<string, unknown>;
    if (action === MACRO_PACKAGE_IPC.socket && !message.header.isReply) {
      const requestId = String(body.requestId), current = this.requests.get(requestId);
      const ownerRequestId = typeof body.uploadOwnerRequestId === 'string' ? body.uploadOwnerRequestId : '';
      const upload = this.uploads.get(ownerRequestId);
      const owns = !!upload && upload.packageId === body.packageId && upload.campaignId === body.campaignId
        && (current === undefined || (current.generation === this.generation && current.ownerRequestId === ownerRequestId));
      try {
        if (!owns) throw new Error('raw_package_upload_not_owned');
        this.requests.set(requestId, { generation: this.generation, ownerRequestId });
        const { uploadOwnerRequestId: _owner, ...payload } = body;
        if (!this.realtime.trySendMessage?.(generateMacroPackageMessage(payload as never))) throw new Error('raw_package_upload_disconnected');
      } catch {
        // Stale/unknown main work must not settle a new upload of the same recording.
        if (owns && upload) { this.uploads.delete(ownerRequestId); this.pruneRequests(ownerRequestId);
          upload.resolve({ ok: false, error: 'raw_package_upload_disconnected' }); }
        // Reject the exact pending main-process exchange immediately; never queue it into another owner session.
        this.ipc.send(playcastEnvelope('host', MACRO_PACKAGE_IPC.socket, true, { message: generateMacroPackageMessage({
          type: RawPackageMessageType.INGESTED, requestId, campaignId: String(body.campaignId), packageId: String(body.packageId),
          ok: false, error: 'forbidden',
        }) }));
      }
      return true;
    }
    if (action !== MACRO_PACKAGE_IPC.uploadComplete || !message.header.isReply) return false;
    const upload = this.uploads.get(String(body.requestId));
    if (!upload) return true;
    this.uploads.delete(String(body.requestId));
    this.pruneRequests(String(body.requestId));
    upload.resolve(body.ok === true ? { ok: true } : { ok: false, error: typeof body.error === 'string' ? body.error : 'raw_package_upload_failed' });
    return true;
  }

  private pruneRequests(ownerRequestId: string): void {
    for (const [id, request] of this.requests) if (request.ownerRequestId === ownerRequestId) this.requests.delete(id);
  }
}
